import { forwardRef } from 'react';
import { cn } from '../cn.js';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

/**
 * A multi-line field.
 *
 * It shares the input's tokens rather than inventing its own, so a textarea beside an input does
 * not sit a pixel out. `field-sizing-content` lets it grow with what is typed where the browser
 * supports it, which is better than a scrollbar inside a three-line box; `min-h` keeps it usable
 * where it does not.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, rows = 3, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(
        'w-full rounded-(--input-radius) px-(--input-padding-inline) py-(--space-xs)',
        'bg-(--color-surface) text-(--color-text-primary)',
        'border border-(--input-border-color)',
        'text-(length:--font-size-body) placeholder:text-(--input-placeholder)',
        'field-sizing-content min-h-(--textarea-min-height)',
        'outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring) focus-visible:ring-offset-2 focus-visible:ring-offset-(--color-surface)',
        'disabled:cursor-not-allowed disabled:opacity-(--opacity-disabled)',
        'read-only:bg-(--color-surface-sunken)',
        invalid && 'border-(--color-danger)',
        className,
      )}
      {...props}
    />
  );
});
